import Button, {
  ButtonProps,
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import { CommentsContext } from "immigram-ui-cmon/src/components/Comments/context";
import Heading, {
  HeadingTag,
} from "immigram-ui-cmon/src/components/Heading/Heading";
import Modal from "immigram-ui-cmon/src/components/Modal/Modal";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import React, { ReactNode, useCallback, useContext } from "react";

import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";

interface FormModalProps {
  applicationSectionStatus?: ApplicationSectionStatusType | null;
  heading: ReactNode;
  children: ReactNode;
  rootClassName?: string;
  innerClassName?: string;
  detailsLink?: string | null;
  detailsLinkProps?: ButtonProps;
  open: boolean;
  fullScreen?: boolean;
  isAdmin?: boolean;
  onCollapse: () => void;
  onSave?: () => void;
  onNext?: () => void;
  hasCompletedButton?: boolean;
  completeButton?: React.ReactElement;
  isDisabled?: boolean;
  isNextButtonVisible?: boolean;
  actionButton?: ReactNode;
  toggleButton?: ReactNode;
  nextButtonProps?: ButtonProps;

  scrollY?: boolean;
  saveText?: string;
  nextText?: string;
  small?: boolean;
  isSelfService?: boolean;
  name?: string;
}

const FormModal = (props: FormModalProps) => {
  const { t } = useTranslation();
  const { setShowedThreadName } = useContext(CommentsContext);

  const handleClose = useCallback(() => {
    setShowedThreadName && setShowedThreadName("");
    props.onCollapse();
  }, [setShowedThreadName, props]);

  return (
    <Modal
      isOpened={props.open}
      fullScreen={props.fullScreen}
      onClose={handleClose}
    >
      <div className="flex min-h-full w-full flex-row overflow-x-hidden">
        <div
          className={clsxm(
            "mr-2 flex h-full w-[496px] flex-col items-start",
            props.fullScreen && "w-full"
          )}
        >
          <div
            className={clsxm(
              "items-left fixed z-10 flex h-full w-full flex-col items-start pt-8 pb-6",
              props.fullScreen && "pt-14 pb-8 xl:w-full",
              props.small ? "sticky" : "fixed",
              props.innerClassName
            )}
          >
            <div className="relative w-full">
              <Heading
                tag={props.fullScreen ? HeadingTag.H2 : HeadingTag.H4}
                className={clsxm(
                  "mt-0 mb-0 px-8 lg:px-8",
                  props.fullScreen &&
                    "mb-2 w-full md:px-8 lg:px-[4.5rem] xl:px-[140px]"
                )}
              >
                {props.heading}
              </Heading>
              <div
                className="pointer-events-none absolute bottom-0 z-[100] h-8 w-full translate-y-full"
                style={{
                  background:
                    "linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 0.01%, rgba(255, 255, 255, 1) 100%)",
                }}
              />
            </div>
            <div
              className={clsxm(
                "w-full px-8 pt-6 lg:px-8",
                props.scrollY !== false && "overflow-y-auto",
                props.fullScreen && "md:px-8 xl:px-[140px]"
              )}
            >
              {props.children}
            </div>
          </div>
          <div
            className={clsxm(
              "left-0 right-0 bottom-0 z-20 flex w-full gap-x-3 rounded-b-3xl bg-white-900 px-8 pt-5 pb-8",
              props.small ? "sticky" : "fixed",
              props.fullScreen &&
                "rounded-b-none md:px-8 lg:px-[4.5rem] xl:w-full xl:px-[140px]"
            )}
          >
            <div
              className="pointer-events-none absolute -top-8 h-8 w-full"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 0.01%, rgba(255, 255, 255, 1) 100%)",
              }}
            />
            {(props.isAdmin || props.isNextButtonVisible) && props.onNext && (
              <Button
                className="inline-flex"
                type={ButtonType.Primary}
                onClick={props.onNext}
                {...props.nextButtonProps}
              >
                {props.nextText ?? t("application:formModal.button.nextCard")}
              </Button>
            )}

            {props.isAdmin && props.toggleButton ? props.toggleButton : null}

            {props.detailsLink !== undefined ? (
              props.detailsLink !== null ? (
                <Button
                  className="inline-block"
                  href={props.detailsLink}
                  type={ButtonType.Primary}
                  {...props.detailsLinkProps}
                >
                  {props.applicationSectionStatus ===
                  ApplicationSectionStatusType.ReviewCompleted
                    ? t("application:formModal.button.openProjects")
                    : t("application:formModal.button.describeProjects")}
                </Button>
              ) : (
                <Button disabled={true} type={ButtonType.Primary}>
                  {props.applicationSectionStatus ===
                  ApplicationSectionStatusType.ReviewCompleted
                    ? t("application:formModal.button.openProjects")
                    : t("application:formModal.button.describeProjects")}
                </Button>
              )
            ) : (!props.isSelfService &&
                props.applicationSectionStatus ===
                  ApplicationSectionStatusType.ReviewCompleted) ||
              (props.isDisabled && !props.isAdmin) ||
              (props.isAdmin && !props.hasCompletedButton) ? (
              <Button
                className="inline-flex"
                type={ButtonType.Secondary}
                onClick={props.onCollapse}
              >
                {t("application:formModal.button.close")}
              </Button>
            ) : (
              <>
                {props.actionButton ? (
                  <>{props.actionButton}</>
                ) : (
                  <>
                    {props.isAdmin &&
                      props.hasCompletedButton &&
                      props.completeButton}

                    {!props.isAdmin && !props.isDisabled && (
                      <Button
                        className="inline-flex"
                        type={ButtonType.Secondary}
                        onClick={props.onSave}
                      >
                        {props.saveText ??
                          t("application:modal.formModal.save")}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FormModal;
